import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { color } from '../../app/theme';
import TextPreview from './previews/TextPreview';
import FilePreview from './previews/FilePreview';
import OptionalCardPreview from './previews/OptionalCardPreview';
import ConfirmCardPreview from './previews/ConfirmCardPreview';
import ImagemapPreview from './previews/ImagemapPreview';
import ImageCardPreview from './previews/ImageCardPreview';
import RichVideoPreview from './previews/RichVideoPreview';

const useStyles = makeStyles(
  () => ({
    root: () => ({
      position: 'fixed',
      top: `79px`,
      right: '0',
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 79px)',
      width: '368px',
      boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
      backgroundColor: '#fcfcfc',
    }),
    header: {
      flex: '1 0 56px',
      display: 'flex',
      alignItems: 'center',
      height: 56,
      fontSize: 17,
      lineHeight: 1,
      color: color.$13,
      backgroundColor: color.$7,
      padding: '0 0 0 16px',
    },
    content: {
      flex: '1 1 100%',
      padding: '16px',
      overflowY: 'auto',
    },
    shareMessage: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      width: '100%',
      display: 'flex',
      padding: '8px 12px 5px',
      maxWidth: '250px',
      minHeight: '52px',
      alignItems: 'center',
      borderRadius: '12px',
      backgroundColor: '#EFEFEF',
    },
  }),
  {
    name: 'Preview',
  },
);

function Preview({ messages = [] }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { icon: orgIconURL } = useSelector(store => store.organization);

  const previewComponents = messages.map(message => {
    let component = null;
    switch (message.contentType || message.templateType) {
      case 'text/plain':
        return (
          <TextPreview
            key={`preview-${message.index}`}
            orgIconURL={orgIconURL}
            message={message}
          />
        );
      case 'application/x-image':
      case 'application/x-audio':
      case 'application/x-video':
        return (
          <FilePreview
            key={`preview-${message.index}`}
            orgIconURL={orgIconURL}
            message={message}
          />
        );
      case 'application/x-template':
        if (message.data.templateType === 'card') {
          component = (
            <OptionalCardPreview
              key={`preview-${message.index}`}
              orgIconURL={orgIconURL}
              message={message}
            />
          );
        }
        if (message.data.templateType === 'confirm') {
          component = (
            <ConfirmCardPreview
              key={`preview-${message.index}`}
              orgIconURL={orgIconURL}
              message={message}
            />
          );
        }

        if (message.templateType === 'imagemap') {
          component = (
            <ImagemapPreview
              key={`preview-${message.index}`}
              message={message}
            />
          );
        }
        return component;
      case 'card':
        return (
          <OptionalCardPreview
            key={`preview-${message.index}`}
            orgIconURL={orgIconURL}
            message={message}
          />
        );
      case 'image':
        return (
          <ImageCardPreview
            key={`preview-${message.index}`}
            orgIconURL={orgIconURL}
            message={message}
          />
        );
      case 'confirm':
        return (
          <ConfirmCardPreview
            key={`preview-${message.index}`}
            orgIconURL={orgIconURL}
            message={message}
          />
        );
      case 'imagemap':
        return (
          <ImagemapPreview key={`preview-${message.index}`} message={message} />
        );
      case 'richvideo':
        return (
          <RichVideoPreview
            key={`preview-${message.index}`}
            message={message}
          />
        );
      default:
        return null;
    }
  });

  return (
    <div className={classes.root}>
      <div className={classes.header}>{t('preview')}</div>
      <div className={classes.content}>{previewComponents}</div>
    </div>
  );
}

export default Preview;
